import { httpClient, unwrap, unwrapPaginated } from '@/services/httpClient';
import type {
  Branch,
  BranchOption,
  Department,
  DepartmentOption,
  Designation,
  Organization,
  OrganizationSettings,
} from '@/features/organization/types';

export interface UpdateOrganizationPayload {
  name?: string;
  legal_name?: string;
  industry?: string;
  company_size?: string;
  timezone?: string;
  currency?: string;
  website?: string;
  gst_number?: string;
  pan_number?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
}

export interface BranchPayload {
  name: string;
  code?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  phone?: string;
  email?: string;
  is_headquarters?: boolean;
}

export interface DepartmentPayload {
  name: string;
  code?: string;
  branch_id?: number | null;
  parent_department_id?: number | null;
  head_user_id?: number | null;
}

export interface DesignationPayload {
  title: string;
  department_id?: number | null;
  level?: number;
  status?: 'active' | 'inactive';
}

export const organizationApi = {
  get: () => unwrap<Organization>(httpClient.get('/organizations/current')),

  update: (payload: UpdateOrganizationPayload) => unwrap<Organization>(httpClient.put('/organizations/current', payload)),

  uploadLogo: (file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    return unwrap<{ logo_url: string }>(
      httpClient.post('/organizations/current/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    );
  },

  destroy: (confirmName: string) =>
    unwrap<null>(httpClient.delete('/organizations/current', { data: { confirm_name: confirmName } })),

  getSettings: () => unwrap<OrganizationSettings>(httpClient.get('/organizations/current/settings')),

  updateSettings: (payload: Partial<OrganizationSettings>) =>
    unwrap<OrganizationSettings>(httpClient.put('/organizations/current/settings', payload)),
};

export const branchesApi = {
  list: (page: number, perPage: number, search: string, sort: string, order: 'asc' | 'desc') =>
    unwrapPaginated<Branch>(httpClient.get('/branches', { params: { page, per_page: perPage, search: search || undefined, sort, order } })),

  all: () => unwrap<BranchOption[]>(httpClient.get('/branches', { params: { per_page: 100 } })).catch(() => [] as BranchOption[]),

  create: (payload: BranchPayload) => unwrap<Branch>(httpClient.post('/branches', payload)),

  update: (id: number, payload: Partial<BranchPayload>) => unwrap<Branch>(httpClient.put(`/branches/${id}`, payload)),

  remove: (id: number) => unwrap<null>(httpClient.delete(`/branches/${id}`)),
};

export const departmentsApi = {
  list: (page: number, perPage: number, search: string, sort: string, order: 'asc' | 'desc', branchId?: number) =>
    unwrapPaginated<Department>(
      httpClient.get('/departments', { params: { page, per_page: perPage, search: search || undefined, sort, order, branch_id: branchId || undefined } }),
    ),

  all: () => unwrap<DepartmentOption[]>(httpClient.get('/departments', { params: { per_page: 100 } })).catch(() => [] as DepartmentOption[]),

  create: (payload: DepartmentPayload) => unwrap<Department>(httpClient.post('/departments', payload)),

  update: (id: number, payload: Partial<DepartmentPayload>) => unwrap<Department>(httpClient.put(`/departments/${id}`, payload)),

  remove: (id: number) => unwrap<null>(httpClient.delete(`/departments/${id}`)),
};

export const designationsApi = {
  list: (page: number, perPage: number, search: string, sort: string, order: 'asc' | 'desc', departmentId?: number) =>
    unwrapPaginated<Designation>(
      httpClient.get('/designations', { params: { page, per_page: perPage, search: search || undefined, sort, order, department_id: departmentId || undefined } }),
    ),

  all: () => unwrap<Designation[]>(httpClient.get('/designations', { params: { per_page: 100 } })).catch(() => [] as Designation[]),

  create: (payload: DesignationPayload) => unwrap<Designation>(httpClient.post('/designations', payload)),

  update: (id: number, payload: Partial<DesignationPayload>) => unwrap<Designation>(httpClient.put(`/designations/${id}`, payload)),

  remove: (id: number) => unwrap<null>(httpClient.delete(`/designations/${id}`)),
};
